import { useState } from "react";
import Modal from "../../layouts/Modal";
import { EngineCondition, BodyCondition, ISalvageRuleTestResult } from "../../models/PriceSettings";
import useApiHelper from "../../hooks/useApiHelper";
import PriceFormat from "../../utils/priceFormat";
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  BeakerIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

interface SalvageRuleTestModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const engineConditionOptions = [
  { id: EngineCondition.NoFault, label: "Good (No Fault)" },
  { id: EngineCondition.MinorFault, label: "Average (Minor Fault)" },
  { id: EngineCondition.MajorFault, label: "Bad (Major Fault)" },
];

const bodyConditionOptions = [
  { id: BodyCondition.Good, label: "Good" },
  { id: BodyCondition.Average, label: "Average" },
  { id: BodyCondition.HeavyDamage, label: "Bad (Heavy Damage)" },
];

function describeBand(from: number | null, to: number | null) {
  if (from === null && to === null) return "Any trade poor";
  if (from === null) return `Up to ${PriceFormat(to!)}`;
  if (to === null) return `${PriceFormat(from)} and above`;
  return `${PriceFormat(from)} to ${PriceFormat(to)}`;
}

function GateTag({ label, colour }: { label: string; colour: "red" | "amber" | "green" | "gray" }) {
  const colours = {
    red:   "bg-red-100 text-red-700 border border-red-200",
    amber: "bg-amber-100 text-amber-700 border border-amber-200",
    green: "bg-green-100 text-green-700 border border-green-200",
    gray:  "bg-gray-100 text-gray-600 border border-gray-200",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colours[colour]}`}>
      {label}
    </span>
  );
}

function ResultRow({ name, isFixed, value, baseValue, isAddition }: {
  name: string; isFixed: boolean; value: number; baseValue: number; isAddition: boolean;
}) {
  const display = isFixed ? PriceFormat(value) : `${value}% of ${PriceFormat(baseValue)}`;
  const effectLabel = isAddition ? "+" : "-";
  const effectColour = isAddition ? "text-green-600" : "text-red-600";
  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50 border border-gray-100">
      <span className="text-sm text-gray-700">{name}</span>
      <span className={`text-sm font-semibold ${effectColour}`}>
        {effectLabel} {display}
      </span>
    </div>
  );
}

export default function SalvageRuleTestModal({ open, setOpen }: SalvageRuleTestModalProps) {
  const { post } = useApiHelper();

  const [vrm, setVrm] = useState("");
  const [engineCondition, setEngineCondition] = useState<EngineCondition>(EngineCondition.NoFault);
  const [bodyCondition, setBodyCondition] = useState<BodyCondition>(BodyCondition.Good);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ISalvageRuleTestResult | null>(null);

  const handleClose = () => {
    setOpen(false);
    setResult(null);
    setError(null);
    setVrm("");
    setEngineCondition(EngineCondition.NoFault);
    setBodyCondition(BodyCondition.Good);
  };

  const handleTest = async () => {
    const trimmed = vrm.trim().toUpperCase();
    if (!trimmed) {
      setError("Please enter a VRM.");
      return;
    }
    setError(null);
    setResult(null);
    setIsLoading(true);
    try {
      const response = await post<{ vrm: string; engineCondition: EngineCondition; bodyCondition: BodyCondition }, { success: boolean; data: ISalvageRuleTestResult }>(
        "/priceSetting/salvage/test",
        { vrm: trimmed, engineCondition, bodyCondition }
      );
      if (response?.data) {
        setResult(response.data);
      } else {
        setError("Unexpected response from server.");
      }
    } catch (err: any) {
      setError(err?.message ?? "An error occurred while testing the rules.");
    } finally {
      setIsLoading(false);
    }
  };

  const isGated = result && !result.hasSalvageValue;
  const allCalculations = result
    ? [...(result.tradePoorCalculations ?? []), ...(result.salvageCalculations ?? [])]
    : [];

  return (
    <Modal width="max-w-2xl" open={open} setOpen={handleClose}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <div className="flex-shrink-0 p-2 rounded-lg bg-primary-50">
          <BeakerIcon className="h-5 w-5 text-primary-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Test Salvage Rules</h2>
          <p className="text-sm text-gray-500">Check how a VRM would be priced under the current salvage rules.</p>
        </div>
      </div>

      <div className="mt-4 border-t border-gray-200 pt-4 space-y-4">
        {/* VRM Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">VRM</label>
          <input
            type="text"
            value={vrm}
            onChange={(e) => setVrm(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && handleTest()}
            placeholder="e.g. AB12 CDE"
            maxLength={10}
            className="w-full text-sm text-gray-900 border border-gray-300 rounded-md shadow-sm px-3 py-2 uppercase placeholder:normal-case focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Conditions */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Engine Condition</label>
            <select
              value={engineCondition}
              onChange={(e) => setEngineCondition(Number(e.target.value) as EngineCondition)}
              className="w-full text-sm text-gray-900 border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {engineConditionOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Body Condition</label>
            <select
              value={bodyCondition}
              onChange={(e) => setBodyCondition(Number(e.target.value) as BodyCondition)}
              className="w-full text-sm text-gray-900 border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {bodyConditionOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
            <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Action button */}
        <button
          type="button"
          onClick={handleTest}
          disabled={isLoading}
          className="w-full rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:bg-gray-400 transition-colors"
        >
          {isLoading ? "Testing..." : "Run Test"}
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="mt-5 border-t border-gray-200 pt-5 space-y-4">

          {/* Vehicle info */}
          {(result.make || result.model || result.year) && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
              <TruckIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-800">
                  {[result.make, result.model].filter(Boolean).join(" ")}
                  {result.year ? ` (${result.year})` : ""}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {result.vrm}
                  {result.odometerInMiles > 0 && ` · ${result.odometerInMiles.toLocaleString()} miles`}
                  {result.tradePoor > 0 && ` · Trade Poor: ${PriceFormat(result.tradePoor)}`}
                </p>
              </div>
            </div>
          )}

          {/* Status banner */}
          <div className={`flex items-start gap-3 p-4 rounded-lg border ${result.hasSalvageValue ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
            {result.hasSalvageValue
              ? <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
              : <XCircleIcon className="h-6 w-6 text-red-500 flex-shrink-0" />
            }
            <div>
              {result.hasSalvageValue ? (
                <>
                  <p className="text-sm font-semibold text-green-800">Salvage value calculated</p>
                  <p className="text-2xl font-bold text-green-700 mt-1">{PriceFormat(result.finalValue)}</p>
                </>
              ) : (
                <>
                  <p className="text-sm font-semibold text-red-800">No salvage value — gated</p>
                  {result.gatingReason && (
                    <p className="text-sm text-red-700 mt-0.5">{result.gatingReason}</p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Gating flags */}
          {isGated && (
            <div className="flex flex-wrap gap-2">
              {result.gatedByDoesSalvage && <GateTag label="Salvage disabled for customer" colour="red" />}
              {result.gatedByMileage && (
                <GateTag label={`Mileage gate: max ${result.maxSalvageMileage.toLocaleString()} mi`} colour="red" />
              )}
              {result.gatedByZeroManufacturerValue && <GateTag label="Zero manufacturer value" colour="red" />}
              {result.gatedByZeroFinalValue && <GateTag label="Final value was zero/negative" colour="red" />}
              {result.manufacturerYearGated && <GateTag label="Manufacturer year condition not met" colour="amber" />}
              {result.manufacturerValueGated && <GateTag label="Trade poor outside manufacturer band" colour="amber" />}
            </div>
          )}

          {/* Manufacturer match */}
          {result.manufacturerMatch && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm px-3 py-2 rounded-lg bg-primary-50 border border-primary-100">
                <span className="text-primary-700 font-medium">Manufacturer match</span>
                <span className="text-primary-900 font-semibold">{result.manufacturerMatch}</span>
              </div>
              <div className="flex items-center justify-between text-sm px-3 py-2 rounded-lg bg-primary-50 border border-primary-100">
                <span className="text-primary-700 font-medium">Trade poor band</span>
                <span className="text-primary-900 font-semibold">
                  {describeBand(result.manufacturerFrom, result.manufacturerTo)}
                </span>
              </div>
            </div>
          )}

          {/* Calculations */}
          {allCalculations.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Rules applied</p>
              <div className="space-y-1.5">
                {allCalculations.map((calc, i) => (
                  <ResultRow
                    key={i}
                    name={calc.name}
                    isFixed={calc.isFixed}
                    value={calc.value}
                    baseValue={calc.baseValue}
                    isAddition={calc.isAddition}
                  />
                ))}
              </div>
            </div>
          )}

          {allCalculations.length === 0 && result.hasSalvageValue === false && !result.gatingReason && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0" />
              <p className="text-sm text-amber-700">No rules were applied. The vehicle may not have valuation data.</p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
